import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { Header, Footer } from "../App";

const EMPTY_DECK = {
  name: "", type: "Oracle", author: "", link: "", description: "",
  need: [], flavor: [], wisdom_tradition: [], art_style: [], color_story: [],
  teaching_score: 3, recognition: "Hidden gem", curator_pick: false,
  purchase_signal: "", pair_group: "",
};
const EMPTY_EXTRA = { name: "", author: "", link: "", description: "", kind: "accessory" };

function Field({ label, value, onChange, textarea = false, type = "text" }) {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function listToText(v) { return (v || []).join(", "); }
function textToList(v) { return v.split(",").map((s) => s.trim()).filter(Boolean); }

function CatalogTab({ kind }) {
  const isDeck = kind === "decks";
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // object being edited, or {} for new
  const [error, setError] = useState("");
  const [importResult, setImportResult] = useState("");
  const fileRef = useRef(null);

  const importFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const parsed = JSON.parse(await f.text());
      const res = await api("/admin/catalog/import", { method: "POST", body: JSON.stringify(parsed) });
      setError("");
      setImportResult(`Import done: ${res.decks_updated} decks updated, ${res.decks_inserted} added · ${res.extras_updated} extras updated, ${res.extras_inserted} added.`);
      load();
    } catch (err) {
      setImportResult("");
      setError("Import failed: " + err.message);
    }
    e.target.value = "";
  };

  const load = () => api("/admin/decks").then((d) => setItems(isDeck ? d.decks : d.extras)).catch((e) => setError(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const save = async () => {
    setError("");
    try {
      const body = { ...editing };
      if (isDeck) {
        ["need", "flavor", "wisdom_tradition", "art_style", "color_story"].forEach((k) => {
          if (typeof body[k] === "string") body[k] = textToList(body[k]);
        });
        body.teaching_score = Number(body.teaching_score) || 0;
        body.curator_pick = !!body.curator_pick;
        if (!body.pair_group) body.pair_group = null;
      }
      const isNew = !body.id;
      if (isNew) delete body.id;
      await api(`/admin/${kind}${isNew ? "" : `/${body.id || editing.id}`}`, {
        method: isNew ? "POST" : "PUT",
        body: JSON.stringify(isNew ? body : { ...body, id: undefined }),
      });
      setEditing(null);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}" permanently? (Use Retire to park it without deleting.)`)) return;
    await api(`/admin/${kind}/${item.id}`, { method: "DELETE" }).catch((e) => setError(e.message));
    load();
  };

  const [selected, setSelected] = useState(new Set());
  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const setRetired = async (ids, retired) => {
    setError("");
    try {
      await api("/admin/catalog/retire", { method: "POST", body: JSON.stringify({ kind, ids, retired }) });
      setSelected(new Set());
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} item(s) permanently? (Use Retire to park them without deleting.)`)) return;
    setError("");
    try {
      await api("/admin/catalog/delete", { method: "POST", body: JSON.stringify({ kind, ids: [...selected] }) });
      setSelected(new Set());
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const setF = (k, v) => setEditing((prev) => ({ ...prev, [k]: v }));

  return (
    <div data-testid={`admin-${kind}-tab`}>
      {error && <p className="auth-error" data-testid="admin-error">{error}</p>}
      {importResult && <p className="dash-note" data-testid="import-result">{importResult}</p>}
      {!editing && (
        <div className="bulk-bar" data-testid="catalog-toolbar" style={{ marginBottom: 18 }}>
          <button className="mini-btn" data-testid={`add-${kind}-button`}
            onClick={() => setEditing(isDeck ? { ...EMPTY_DECK } : { ...EMPTY_EXTRA })}>
            {isDeck ? "+ Add Deck" : "+ Add Extra"}
          </button>
          {isDeck && (
            <>
              <button className="mini-btn" data-testid="import-catalog-button"
                onClick={() => fileRef.current?.click()}>
                Bulk Add / Upload (JSON)
              </button>
              <input ref={fileRef} type="file" accept=".json,application/json"
                data-testid="import-catalog-input" style={{ display: "none" }} onChange={importFile} />
            </>
          )}
          <span className="num" data-testid="selected-count">{selected.size} selected</span>
          <button className="mini-btn" data-testid="bulk-retire-button" disabled={!selected.size}
            onClick={() => setRetired([...selected], true)}>Retire selected</button>
          <button className="mini-btn" data-testid="bulk-restore-button" disabled={!selected.size}
            onClick={() => setRetired([...selected], false)}>Restore selected</button>
          <button className="mini-btn danger" data-testid="bulk-delete-button" disabled={!selected.size}
            onClick={bulkDelete}>Delete selected</button>
        </div>
      )}

      {editing && (
        <div className="admin-form" data-testid="admin-edit-form">
          <h3>{editing.id ? "Edit" : "Add"} {isDeck ? "Deck" : "Extra"}</h3>
          <Field label="Name" value={editing.name} onChange={(v) => setF("name", v)} />
          <div className="row2">
            {isDeck ? (
              <div className="field">
                <label className="field-label">Type</label>
                <select value={editing.type} onChange={(e) => setF("type", e.target.value)} data-testid="deck-type-select">
                  <option>Oracle</option><option>Tarot</option><option>Hybrid</option>
                </select>
              </div>
            ) : (
              <div className="field">
                <label className="field-label">Kind</label>
                <select value={editing.kind} onChange={(e) => setF("kind", e.target.value)} data-testid="extra-kind-select">
                  <option value="accessory">Accessory</option>
                  <option value="book">Companion Book</option>
                </select>
              </div>
            )}
            <Field label="Author" value={editing.author || ""} onChange={(v) => setF("author", v)} />
          </div>
          <Field label="Affiliate link" value={editing.link || ""} onChange={(v) => setF("link", v)} />
          <Field label="Description" textarea value={editing.description || ""} onChange={(v) => setF("description", v)} />
          {isDeck && (
            <>
              <div className="row2">
                <Field label="Need (comma-separated)" value={listToText(editing.need)} onChange={(v) => setF("need", v)} />
                <Field label="Vibe / flavor" value={listToText(editing.flavor)} onChange={(v) => setF("flavor", v)} />
                <Field label="Wisdom tradition" value={listToText(editing.wisdom_tradition)} onChange={(v) => setF("wisdom_tradition", v)} />
                <Field label="Art style" value={listToText(editing.art_style)} onChange={(v) => setF("art_style", v)} />
                <Field label="Color story" value={listToText(editing.color_story)} onChange={(v) => setF("color_story", v)} />
                <Field label="Teaching score (0-4)" type="number" value={editing.teaching_score ?? 3} onChange={(v) => setF("teaching_score", v)} />
              </div>
              <div className="row2">
                <div className="field">
                  <label className="field-label">Recognition tier</label>
                  <select value={editing.recognition} onChange={(e) => setF("recognition", e.target.value)} data-testid="deck-recognition-select">
                    <option>Bestseller</option><option>Well-loved</option><option>Hidden gem</option>
                  </select>
                </div>
                <Field label="Pair group (optional)" value={editing.pair_group || ""} onChange={(v) => setF("pair_group", v)} />
              </div>
              <Field label="Purchase signal note" value={editing.purchase_signal || ""} onChange={(v) => setF("purchase_signal", v)} />
              <label className="check-field">
                <input type="checkbox" data-testid="deck-curator-checkbox" checked={!!editing.curator_pick}
                  onChange={(e) => setF("curator_pick", e.target.checked)} />
                Curator's Pick (+3 match bonus)
              </label>
            </>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button className="stamp-btn solid" data-testid="admin-save-button" onClick={save}>Save</button>
            <button className="stamp-btn" data-testid="admin-cancel-button" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {items === null ? (
        <p className="hint">Loading…</p>
      ) : (
        <div className="report-scroll">
        <table className="admin-table narrow" data-testid={`admin-${kind}-table`}>
          <thead>
            <tr>
              <th><input type="checkbox" data-testid="select-all-checkbox"
                checked={items.length > 0 && items.every((i) => selected.has(i.id))}
                onChange={(e) => setSelected(e.target.checked ? new Set(items.map((i) => i.id)) : new Set())} /></th>
              <th>Name</th><th>{isDeck ? "Author" : "Kind"}</th>{isDeck && <th>Tier</th>}<th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className={item.retired ? "row-retired" : ""} data-testid={`admin-row-${item.name}`}>
                <td><input type="checkbox" data-testid={`select-${item.name}`}
                  checked={selected.has(item.id)} onChange={() => toggleSelect(item.id)} /></td>
                <td>{item.name}{item.retired ? <span className="retired-badge">Retired</span> : null}</td>
                <td>{isDeck ? (item.author || "-") : item.kind}</td>
                {isDeck && <td>{item.recognition}{item.curator_pick ? " ★" : ""}</td>}
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="mini-btn" data-testid={`edit-${item.name}`}
                    onClick={() => setEditing({ ...item })}>Edit</button>
                  <button className="mini-btn" data-testid={`retire-${item.name}`}
                    onClick={() => setRetired([item.id], !item.retired)}>{item.retired ? "Restore" : "Retire"}</button>
                  <button className="mini-btn danger" data-testid={`delete-${item.name}`}
                    onClick={() => remove(item)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

// ---------------- Admins tab ----------------
function AdminsTab() {
  const [admins, setAdmins] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = () => api("/admin/admins").then((d) => setAdmins(d.admins)).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  const flash = (m) => { setNote(m); setError(""); };
  const fail = (e) => { setError(e.message); setNote(""); };

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api("/admin/admins", { method: "POST", body: JSON.stringify({ name, email }) });
      flash(`Invitation sent to ${email}. It expires in 24 hours.`);
      setName(""); setEmail("");
      load();
    } catch (err) { fail(err); }
    finally { setBusy(false); }
  };

  const resend = async (a) => {
    try {
      await api(`/admin/admins/${a.id}/resend`, { method: "POST" });
      flash(`Invitation resent to ${a.email} (fresh 24-hour link).`);
    } catch (e) { fail(e); }
  };

  const saveEdit = async (a) => {
    try {
      await api(`/admin/admins/${a.id}`, { method: "PUT", body: JSON.stringify({ name: editName }) });
      setEditId(null);
      load();
    } catch (e) { fail(e); }
  };

  const retire = async (a) => {
    try {
      await api(`/admin/admins/${a.id}/retire`, { method: "POST", body: JSON.stringify({ retired: a.status !== "retired" }) });
      load();
    } catch (e) { fail(e); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.email} as an admin? Their account becomes a regular user account.`)) return;
    try {
      await api(`/admin/admins/${a.id}`, { method: "DELETE" });
      load();
    } catch (e) { fail(e); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div data-testid="admin-admins-tab">
      <p className="dash-note">
        Admins always sign in via magic link (sessions last 48 hours). Invitations expire in 24 hours.
      </p>
      {error && <p className="auth-error" data-testid="admins-error">{error}</p>}
      {note && <p className="dash-note" data-testid="admins-note">{note}</p>}

      <form className="admin-form" data-testid="admin-invite-form" onSubmit={invite}>
        <h3>Invite an admin</h3>
        <div className="row2">
          <div className="field">
            <label className="field-label" htmlFor="adm-name">Name</label>
            <input id="adm-name" data-testid="admin-name-input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="adm-email">Email</label>
            <input id="adm-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <button className="stamp-btn solid" data-testid="send-invite-button" type="submit" disabled={busy}>
            {busy ? "Sending…" : "Send Admin Invitation"}
          </button>
        </div>
      </form>

      {admins === null ? <p className="hint">Loading…</p> : (
        <table className="admin-table narrow" data-testid="admins-table">
          <thead><tr><th>Name</th><th>Email</th><th>Admin Since</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.id} className={a.status === "retired" ? "row-retired" : ""} data-testid={`admin-row-${a.email}`}>
                <td>
                  {editId === a.id ? (
                    <>
                      <input value={editName} data-testid={`edit-name-input-${a.email}`}
                        onChange={(e) => setEditName(e.target.value)} style={{ fontSize: 12, padding: "4px 8px" }} />
                      <button className="mini-btn" data-testid={`save-name-${a.email}`} onClick={() => saveEdit(a)}>Save</button>
                    </>
                  ) : (a.name || "-")}
                </td>
                <td>{a.email}</td>
                <td className="num">{a.status === "invited" ? "invited " + fmtDate(a.invited_at) : fmtDate(a.accepted_at)}</td>
                <td>{a.status}{a.status === "retired" ? "" : ""}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {a.status === "invited" && (
                    <button className="mini-btn" data-testid={`resend-${a.email}`} onClick={() => resend(a)}>Resend invite</button>
                  )}
                  {editId !== a.id && (
                    <button className="mini-btn" data-testid={`edit-admin-${a.email}`}
                      onClick={() => { setEditId(a.id); setEditName(a.name || ""); }}>Edit</button>
                  )}
                  {a.email !== "jen@jenniferblumenthal.com" && (
                    <>
                      <button className="mini-btn" data-testid={`retire-admin-${a.email}`} onClick={() => retire(a)}>
                        {a.status === "retired" ? "Restore" : "Retire"}
                      </button>
                      <button className="mini-btn danger" data-testid={`delete-admin-${a.email}`} onClick={() => remove(a)}>Delete</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------- Data hygiene (purge test data) ----------------
function PurgePanel() {
  const [keep, setKeep] = useState("jen@jenniferblumenthal.com, jayleonardi2020@gmail.com");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const keepList = () => keep.split(",").map((s) => s.trim()).filter(Boolean);

  const run = async (dryRun) => {
    setBusy(true);
    setError("");
    try {
      const res = await api("/admin/maintenance/purge-users", {
        method: "POST",
        body: JSON.stringify({ keep_emails: keepList(), dry_run: dryRun }),
      });
      if (dryRun) { setPreview(res); setResult(null); }
      else { setResult(res); setPreview(null); }
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="admin-form" data-testid="purge-panel" style={{ marginTop: 26 }}>
      <h3>Data hygiene: purge test accounts</h3>
      <p className="dash-note" style={{ textAlign: "left" }}>
        Deletes every user (and their sessions, quiz results, favorites, owned flags, and click
        events) EXCEPT the emails listed below. Decks and extras are never touched.
      </p>
      <div className="field">
        <label className="field-label">Keep these accounts (comma-separated)</label>
        <input data-testid="purge-keep-input" value={keep} onChange={(e) => setKeep(e.target.value)} />
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <button className="mini-btn" data-testid="purge-preview-button" disabled={busy} onClick={() => run(true)}>
          {busy ? "Working…" : "Preview purge"}
        </button>
        {preview && (
          <button className="mini-btn danger" data-testid="purge-run-button" disabled={busy}
            onClick={() => {
              if (window.confirm(`Permanently delete ${preview.delete_emails.length} account(s) and all their data?`)) run(false);
            }}>
            Delete {preview.delete_emails.length} account(s)
          </button>
        )}
      </div>
      {preview && (
        <div data-testid="purge-preview" style={{ marginTop: 14 }}>
          <p className="dash-note" style={{ textAlign: "left" }}>
            Will delete {preview.delete_emails.length} account(s): {preview.delete_emails.join(", ") || "none"}
          </p>
          <p className="dash-note" style={{ textAlign: "left" }}>
            Rows: {Object.entries(preview.counts).map(([k, v]) => `${k}: ${v}`).join(" · ")}
          </p>
        </div>
      )}
      {result && (
        <p className="dash-note" data-testid="purge-result" style={{ textAlign: "left" }}>
          Purge complete. Remaining accounts: {result.remaining_users.join(", ")}.
        </p>
      )}
      {error && <p className="auth-error" data-testid="purge-error">{error}</p>}
    </div>
  );
}

// ---------------- Reports ----------------
const REPORTS = ["Favorites", "Owned", "Quizzes", "Users"];

const REPORT_COLS = {
  Favorites: {
    csv: "favorites",
    cols: [
      ["name", "Deck"], ["author", "Author"], ["kind", "Type"],
      ["need", "Need"], ["flavor", "Vibe"], ["wisdom_tradition", "Tradition"],
      ["art_style", "Art Style"], ["color_story", "Color Story"],
      ["teaching_score", "Teach"], ["recognition", "Tier"], ["purchase_signal", "Purchase Signal"],
      ["curator_pick", "Curator's Pick"], ["count_all", "Count All"], ["count_unique", "Count Unique"],
    ],
  },
  Owned: {
    csv: "owned",
    cols: [
      ["name", "Deck"], ["author", "Author"], ["kind", "Type"],
      ["need", "Need"], ["flavor", "Vibe"], ["wisdom_tradition", "Tradition"],
      ["art_style", "Art Style"], ["color_story", "Color Story"],
      ["teaching_score", "Teach"], ["recognition", "Tier"], ["purchase_signal", "Purchase Signal"],
      ["curator_pick", "Curator's Pick"], ["count_all", "Count All"], ["count_unique", "Count Unique"],
    ],
  },
  Quizzes: {
    csv: "quizzes",
    cols: [
      ["id", "Row ID"], ["user_name", "User"], ["user_email", "Email"], ["quiz_number", "Quiz #"],
      ["date", "Date"], ["time", "Time (UTC)"],
      ["oracle_deck", "Oracle BFF Deck"], ["oracle_author", "Oracle Author"], ["oracle_cta", "Oracle CTA Clicked"],
      ["surprise_deck", "Surprise BFF Deck"], ["surprise_author", "Surprise Author"], ["surprise_cta", "Surprise CTA Clicked"],
      ["wildcard_deck", "Wildcard BFF Deck"], ["wildcard_author", "Wildcard Author"], ["wildcard_cta", "Wildcard CTA Clicked"],
      ["challenge", "5-Day Challenge Clicked"],
    ],
  },
  Users: {
    csv: "users",
    cols: [["name", "Name"], ["email", "Email"], ["permissions", "Permissions"], ["quiz_completed", "Quiz Completed"]],
  },
};

function cellVal(v) {
  if (Array.isArray(v)) return v.join(", ");
  if (v === true) return "★";
  if (v === false || v === null || v === undefined || v === "") return "-";
  return String(v);
}

function ReportPanel({ report }) {
  const [rows, setRows] = useState(null);
  const cfg = REPORT_COLS[report];

  useEffect(() => {
    setRows(null);
    api(`/admin/reports/${cfg.csv}`).then((d) => setRows(d.rows)).catch(() => setRows([]));
  }, [report]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = () => {
    if (!rows) return;
    const esc = (s) => `"${String(Array.isArray(s) ? s.join(", ") : s ?? "").replace(/"/g, '""')}"`;
    const csv = [cfg.cols.map(([, label]) => esc(label)).join(","),
      ...rows.map((r) => cfg.cols.map(([k]) => esc(r[k])).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `oracle-bff-${cfg.csv}-report.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid={`report-${cfg.csv}`}>
      <div style={{ textAlign: "center", marginBottom: 14 }}>
        <button className="mini-btn" data-testid="report-csv-button" onClick={exportCsv}>Download CSV</button>
      </div>
      {rows === null ? <p className="hint">Loading…</p> : rows.length === 0 ? (
        <p className="hint" data-testid="report-empty">Nothing here yet.</p>
      ) : (
        <div className="report-scroll">
          <table className="admin-table" data-testid={`report-${cfg.csv}-table`}>
            <thead><tr>{cfg.cols.map(([k, label]) => <th key={k}>{label}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || r.email || i} data-testid={`report-row-${i}`}>
                  {cfg.cols.map(([k]) => <td key={k}>{cellVal(r[k])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("decks"); // decks | extras | admins | report
  const [report, setReport] = useState("Favorites");
  const [favCount, setFavCount] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    api("/favorites").then((f) => setFavCount(f.favorites.length)).catch(() => {});
  }, []);
  return (
    <div className="frame admin-frame">
      <Header favCount={favCount} onFavClick={() => navigate("/?favs=1")} />
      <div className="results-head" data-testid="admin-panel">
        <div className="eyebrow">Admin</div>
        <h2>Deck Drawer Admin</h2>
        <p>Manage the catalog, admins, and reports.</p>
      </div>
      <div className="admin-tabs">
        <button className={`admin-tab ${tab === "decks" ? "active" : ""}`} data-testid="tab-decks" onClick={() => setTab("decks")}>Decks</button>
        <button className={`admin-tab ${tab === "extras" ? "active" : ""}`} data-testid="tab-extras" onClick={() => setTab("extras")}>Extras</button>
        <button className={`admin-tab ${tab === "admins" ? "active" : ""}`} data-testid="tab-admins" onClick={() => setTab("admins")}>Admins</button>
        <span className="reports-label">Reports</span>
        <select data-testid="reports-select" value={tab === "report" ? report : ""}
          onChange={(e) => { setReport(e.target.value); setTab("report"); }}>
          <option value="" disabled>Choose a report…</option>
          {REPORTS.map((r) => <option key={r} value={r} data-testid={`report-option-${r}`}>{r}</option>)}
        </select>
      </div>
      {tab === "decks" && <CatalogTab kind="decks" />}
      {tab === "extras" && <CatalogTab kind="extras" />}
      {tab === "admins" && <><AdminsTab /><PurgePanel /></>}
      {tab === "report" && <ReportPanel report={report} />}
      <Footer />
    </div>
  );
}
